import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import ModalPop from 'components/sales/modal/modal';
import FormSection from 'components/sales/formSection/formSection';
import { uploadAdditionalAchievements, SalesTargetsApiError } from 'services/api/salesTargetsApi';
import DocumentUploadButton from 'services/utils/fileUpload/DocumentUploadButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileArrowDown } from '@fortawesome/free-solid-svg-icons';
import './salesTargetsListing.css';

const SAMPLE_ADDITIONAL_ACHIEVEMENT_CSV = `employee_email,amount,reason,reference_id
raju@sss.com,-100000,Contract aborted last 2 months,CONTRACT-123
raju@sss.com,300000,Ad sales,AD-456`;

const downloadSampleCsv = () => {
  const blob = new Blob([SAMPLE_ADDITIONAL_ACHIEVEMENT_CSV], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'Sales_Additional_Achievement_sample.csv';
  link.click();
  URL.revokeObjectURL(url);
};

const getErrorMessage = (err) => {
  const msg = err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message;
  return msg && typeof msg === 'string' ? msg : 'Something went wrong. Please try again.';
};

const formatSuccessMessage = (data) => {
  const base = data?.message || 'Additional achievements processed successfully';
  const created = Number(data?.created);
  const updated = Number(data?.updated);
  const parts = [];
  if (!Number.isNaN(created)) parts.push(`${created} created`);
  if (!Number.isNaN(updated)) parts.push(`${updated} updated`);
  return parts.length ? `${base} (${parts.join(', ')}).` : base;
};

const AddAchievementPopup = ({ show, onClose, onSuccess }) => {
  const [csvUrl, setCsvUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitRowErrors, setSubmitRowErrors] = useState([]);
  const [submitSuccess, setSubmitSuccess] = useState('');

  useEffect(() => {
    if (!show) return;
    setCsvUrl('');
    setSubmitError('');
    setSubmitRowErrors([]);
    setSubmitSuccess('');
    setIsUploading(false);
  }, [show]);

  const handleClose = () => {
    setCsvUrl('');
    setSubmitError('');
    setSubmitRowErrors([]);
    setSubmitSuccess('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!csvUrl?.trim()) return;

    setSubmitError('');
    setSubmitRowErrors([]);
    setSubmitSuccess('');
    setIsSubmitting(true);
    try {
      const result = await uploadAdditionalAchievements({ csv_url: csvUrl.trim() });
      setSubmitSuccess(formatSuccessMessage(result));
      if (typeof onSuccess === 'function') onSuccess(result);
    } catch (err) {
      setSubmitError(err?.message || getErrorMessage(err));
      if (err instanceof SalesTargetsApiError && err.rowErrors?.length) {
        setSubmitRowErrors(err.rowErrors);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = Boolean(csvUrl?.trim()) && !isUploading;

  return (
    <ModalPop
      title="Add Achievement"
      show={show}
      handleModalHide={handleClose}
      keyboard
      backdrop
      modalSize="compact"
      centered
      headerClassName="set-targets-modal-header"
      titleClassName="modal-title set-targets-modal-title"
    >
      <FormSection isLoading={isSubmitting}>
        <div className="set-targets-popup set-targets-form">
          {submitError && (
            <div className="alert alert-danger set-targets-popup-error mb-3" role="alert">
              <div className="set-targets-error-msg">{submitError}</div>
              {submitRowErrors.length > 0 && (
                <table className="set-targets-row-errors">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Email</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submitRowErrors.map((e) => (
                      <tr key={`${e.row}-${e.email}`}>
                        <td>{e.row}</td>
                        <td>{e.email}</td>
                        <td>{e.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
          {submitSuccess && (
            <div className="alert alert-success set-targets-popup-error mb-3" role="alert">
              {submitSuccess}
            </div>
          )}

          <div className="set-targets-form-body">
            <div className="set-targets-info-card">
              <div className="set-targets-info-card-label">Additional achievements</div>
              <div className="set-targets-info-card-hint">
                Upload a CSV with employee email, amount, reason, and reference ID to add or adjust
                achievements for sales coordinators.
              </div>
            </div>

            <div className="set-targets-upload-block">
              <div className="set-targets-upload-label">
                CSV file <span className="set-targets-weekly-required">*</span>
              </div>
              <div className="set-targets-upload-row d-flex align-items-center flex-wrap">
                <DocumentUploadButton
                  value={csvUrl}
                  onUpload={(url) => setCsvUrl(url)}
                  onUploadingChange={setIsUploading}
                  accept=".csv,text/csv"
                  uploadOptions={{ service_name: 'salestargets', doc_type: 'document' }}
                  buttonLabel={isUploading ? 'Uploading…' : 'Upload CSV'}
                  uploadedLabel="✓ CSV uploaded"
                  required
                />
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm set-targets-sample-csv-icon d-inline-flex align-items-center justify-content-center"
                  title="Download sample CSV"
                  aria-label="Download sample CSV"
                  onClick={downloadSampleCsv}
                >
                  <FontAwesomeIcon icon={faFileArrowDown} aria-hidden />
                  <span className="ml-1">Sample CSV</span>
                </button>
              </div>
            </div>
          </div>

          <div className="set-targets-form-footer">
            <Button
              className="btn btn-primary btn-sm"
              onClick={handleSubmit}
              disabled={!canSubmit || isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Upload achievements'}
            </Button>
          </div>
        </div>
      </FormSection>
    </ModalPop>
  );
};

AddAchievementPopup.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

AddAchievementPopup.defaultProps = {
  onSuccess: undefined,
};

export default AddAchievementPopup;
